/**
 * http.ts — the bearer-attaching fetch layer for ALL /api/* calls.
 *
 * Modeled on the Reports kit's src/lib/apiClient.ts apiFetch, but markedly
 * simpler (architecture §4): the Shipping module has NO ship-dark build flag
 * (the bearer layer is always on — there is no legacy cookie-only client to
 * stay byte-identical to) and NO print-token second channel (no headless
 * /print/* render tabs here). What remains is the same two-credential model:
 *
 *   - The Entra ACCESS TOKEN (Authorization: Bearer ...) proves WHO is calling.
 *     POST /api/auth/session validates it against the Users allowlist and mints
 *     the `cdx_session` cookie; the API choke-point also reads App Roles off it.
 *   - The `cdx_session` COOKIE is the server's authorization source of truth
 *     (allowlist + roles), exactly as in the Reports kit's link-auth.ts. Every
 *     request therefore carries credentials:'include' so the cookie rides along.
 *
 * Lifecycle on a cold tab: the first /api call goes out with a bearer but no
 * cookie -> the server 401s -> apiFetch mints the session (single-flight, so
 * concurrent 401s from parallel queries trigger ONE mint) -> the request is
 * retried once with a fresh bearer. AuthRoute also fires warmSession() right
 * after sign-in so the first board fetch normally never pays that round trip.
 *
 * Failure posture (mirrors the Reports kit):
 *   - getApiToken NEVER throws and NEVER redirects — a silent-acquisition
 *     failure degrades to cookie-only rather than bouncing a page into a
 *     full-screen Entra navigation mid-render.
 *   - When the silent token fails AND the server keeps 401ing after a mint +
 *     retry, a ONE-SHOT interactive recovery (sessionStorage guard +
 *     acquireTokenRedirect) runs — at most once per browser session, so an
 *     unfixable 401 (e.g. a user not on the allowlist) surfaces as an error
 *     state instead of a redirect loop. The guard re-arms on the next OK
 *     response. This is the Reports kit's reauthOnce shape, minus its
 *     VITE_API_BEARER_ENABLED flag machinery.
 */
import { msalInstance, ensureMsalInitialized, apiTokenRequest, isDevAuthBypass } from '@auth/index';

/**
 * Acquire an Entra access token for the Shipping API scope. Returns null
 * whenever a token cannot be obtained (no cached account, interaction_required,
 * blocked silent iframe, expired SSO), in which case the caller proceeds
 * cookie-only. NEVER throws and NEVER redirects from here — interactive
 * recovery is owned by the 401 path in apiFetch, which only fires when the
 * server actually rejects the call.
 */
export async function getApiToken(): Promise<string | null> {
  try {
    await ensureMsalInitialized();
    const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
    if (!account) return null;
    const result = await msalInstance.acquireTokenSilent({ ...apiTokenRequest, account });
    return result.accessToken || null;
  } catch {
    return null;
  }
}

/**
 * Single-flight guard for session minting. A board page mounts several queries
 * at once; if the cookie is missing they all 401 together, and we want exactly
 * ONE POST /api/auth/session in flight — every concurrent caller awaits the
 * same promise. Cleared in finally so a failed mint can be retried later.
 */
let mintInFlight: Promise<boolean> | null = null;

/**
 * Mint the `cdx_session` cookie: POST /api/auth/session with the Entra bearer
 * attached. The server validates the bearer (signature, audience, tenant),
 * checks the email against the Users allowlist, and Set-Cookies the session;
 * the response body is { ok, identity, roles } but only `ok` matters here —
 * the cookie itself arrives via the Set-Cookie header (credentials:'include').
 * Returns false on any failure (no token, network error, 401/403 from the
 * allowlist gate); never throws.
 */
export function mintSession(): Promise<boolean> {
  if (mintInFlight) return mintInFlight;
  mintInFlight = doMintSession().finally(() => {
    mintInFlight = null;
  });
  return mintInFlight;
}

/**
 * Header names the bearer rides in. X-Ship-Bearer is the one that matters in
 * production: the Azure Static Web Apps proxy REPLACES the standard
 * `Authorization` header with its own internal platform token before the
 * request reaches the managed Functions (verified live 2026-06-09), so a
 * bearer sent only the standard way NEVER arrives. The custom header passes
 * through untouched. Authorization is still attached alongside for local dev
 * (func start / swa start) and any tooling that expects the standard header;
 * the API prefers X-Ship-Bearer (api/src/lib/jwt.ts bearerFromRequest).
 */
const BEARER_HEADER = 'X-Ship-Bearer';

function bearerHeaders(token: string): Record<string, string> {
  const value = `Bearer ${token}`;
  return { [BEARER_HEADER]: value, Authorization: value };
}

async function doMintSession(): Promise<boolean> {
  const token = await getApiToken();
  // No bearer -> the server has nothing to validate; don't waste the request.
  if (!token) return false;
  try {
    const res = await fetch('/api/auth/session', {
      method: 'POST',
      headers: bearerHeaders(token),
      credentials: 'include',
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * warmSession — fire-and-forget session pre-mint, called by AuthRoute right
 * after the MSAL session is confirmed. Pays the mint round trip BEFORE the
 * first data fetch so /api/board doesn't eat a 401 -> mint -> retry on every
 * cold tab. No-op under the dev-mode auth bypass (no Entra app, no API — the
 * mock client serves all data offline).
 */
export function warmSession(): void {
  if (isDevAuthBypass()) return;
  void mintSession();
}

/** Merge a header value into whatever shape the caller passed in init.headers. */
function withHeader(init: RequestInit | undefined, name: string, value: string): Headers {
  const headers = new Headers(init?.headers);
  headers.set(name, value);
  return headers;
}

const REAUTH_GUARD_KEY = 'shp_api_reauth_attempted';
let reauthInFlight = false;

/**
 * One-shot interactive re-auth for the case where silent token acquisition
 * fails AND the server keeps 401ing after a mint + retry. Fires at most ONCE
 * per browser session (the sessionStorage guard is cleared on the next
 * successful response), which prevents a redirect LOOP when the 401 is not
 * fixable by a fresh token (e.g. the user is not on the Users allowlist): the
 * user lands back with an error state rather than looping forever.
 */
async function reauthOnce(): Promise<void> {
  if (reauthInFlight) return;
  try {
    if (sessionStorage.getItem(REAUTH_GUARD_KEY) === '1') return;
  } catch { /* ignore */ }
  reauthInFlight = true;
  try {
    sessionStorage.setItem(REAUTH_GUARD_KEY, '1');
  } catch { /* ignore */ }
  try {
    await ensureMsalInitialized();
    const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
    if (!account) return;
    await msalInstance.acquireTokenRedirect({ ...apiTokenRequest, account });
  } catch {
    // Redirect could not start; leave the 401 to surface in the UI.
  } finally {
    reauthInFlight = false;
  }
}

/**
 * apiFetch — the standard fetch wrapper for ALL /api/* calls.
 *
 * Attaches Authorization: Bearer when a token is available, always preserves
 * credentials:'include' (the cdx_session cookie is the server's authorization
 * source), and merges caller init/headers without clobbering.
 *
 * 401 handling: a 401 means the cookie is missing/expired (or the user fails
 * the allowlist). We mint the session once (single-flight across concurrent
 * 401s) and retry the request ONCE with a freshly acquired bearer. If the
 * server still 401s, the one-shot interactive recovery is kicked off (see
 * reauthOnce) and the 401 response is returned for the caller to surface.
 *
 * NOTE on retry safety: the retry re-sends init.body. The typed client only
 * ever passes JSON strings (never streams), so a second send is always safe.
 */
export async function apiFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const send = async (): Promise<Response> => {
    const token = await getApiToken();
    let headers = new Headers(init?.headers);
    if (token) {
      // Both headers, same value: X-Ship-Bearer survives the SWA proxy (which
      // replaces Authorization with its own platform token); Authorization
      // keeps local dev and standard tooling working. See bearerHeaders().
      headers = withHeader(init, 'Authorization', `Bearer ${token}`);
      headers.set(BEARER_HEADER, `Bearer ${token}`);
    }
    return fetch(input, { ...init, headers, credentials: 'include' });
  };

  let res = await send();

  if (res.status === 401) {
    // Cookie missing/expired: mint (one mint shared by all concurrent 401s),
    // then retry once. The retry calls getApiToken again so it carries a fresh
    // bearer even if the first acquisition silently failed.
    await mintSession();
    res = await send();
    if (res.status === 401) {
      // Mint + retry didn't fix it — silent auth is broken or the user is not
      // allowlisted. Hand off to the one-shot interactive recovery.
      void reauthOnce();
    }
  }

  if (res.ok) {
    // A good response proves the auth path is working; re-arm the one-shot
    // recovery for a future transient failure.
    try {
      sessionStorage.removeItem(REAUTH_GUARD_KEY);
    } catch { /* ignore */ }
  }
  return res;
}
